import { useEffect, useMemo, useState } from 'react';
import { SparkJob, SparkJobStatusUpdate } from '../types/sparkTypes';

export interface ParallelInfo {
  stageName: string;
  activeCount: number;
  completedCount: number;
  pendingCount: number;
  totalCount: number;
}

export interface ProgressAnalysis {
  latestUpdate: SparkJobStatusUpdate | null;
  parallelInfo: ParallelInfo | null;
}

const TERMINAL_DISPLAY_STATUSES = new Set<SparkJob['_status']>(['completed', 'failed', 'cancelled']);

function getVisibleUpdates(updates: SparkJobStatusUpdate[] | undefined): SparkJobStatusUpdate[] {
  return (updates || []).filter((update) => !update.silent);
}

function getLatestVisibleScopeUpdates(
  updates: SparkJobStatusUpdate[] | undefined
): Map<string, SparkJobStatusUpdate> {
  const latestByScope = new Map<string, SparkJobStatusUpdate>();
  for (const update of getVisibleUpdates(updates)) {
    if (!update.scope || update.scope === 'overall') {
      continue;
    }
    latestByScope.set(update.scope, update);
  }
  return latestByScope;
}

function getBranchMetadata(scope: string): { branchScope: string; stageName: string } | null {
  const parts = scope.split('.').filter(Boolean);
  const numericIndex = parts.findIndex((part) => /^\d+$/.test(part));
  if (numericIndex <= 0) {
    return null;
  }

  return {
    branchScope: parts.slice(0, numericIndex + 1).join('.'),
    stageName: parts[numericIndex - 1],
  };
}

function hasActiveParallelBranches(updates: SparkJobStatusUpdate[] | undefined): boolean {
  const branchUpdatesByStage = new Map<string, Map<string, SparkJobStatusUpdate>>();
  const visibleUpdates = getVisibleUpdates(updates).sort(
    (left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
  );

  for (const update of visibleUpdates) {
    if (!update.scope || update.scope === 'overall') {
      continue;
    }

    const branchMetadata = getBranchMetadata(update.scope);
    if (!branchMetadata) {
      continue;
    }

    let branchUpdates = branchUpdatesByStage.get(branchMetadata.stageName);
    if (!branchUpdates) {
      branchUpdates = new Map<string, SparkJobStatusUpdate>();
      branchUpdatesByStage.set(branchMetadata.stageName, branchUpdates);
    }

    branchUpdates.set(branchMetadata.branchScope, update);
  }

  return Array.from(branchUpdatesByStage.values()).some((branchUpdates) => {
    if (branchUpdates.size <= 1) {
      return false;
    }

    return Array.from(branchUpdates.values()).some((update) => update.code === 'in_progress');
  });
}

export function getJobDisplayStatus(job: SparkJob | null | undefined): SparkJob['_status'] | null {
  if (!job) {
    return null;
  }

  if (job._status === 'failed' || job._status === 'cancelled') {
    return job._status;
  }

  if (job._status === 'completed' && hasActiveParallelBranches(job._status_updates)) {
    return 'running';
  }

  return job._status;
}

export function isJobDisplayActive(job: SparkJob | null | undefined): boolean {
  const status = getJobDisplayStatus(job);
  return status === 'pending' || status === 'running';
}

export function isJobDisplayTerminal(job: SparkJob | null | undefined): boolean {
  const status = getJobDisplayStatus(job);
  return status != null && TERMINAL_DISPLAY_STATUSES.has(status);
}

export function useAnimatedProgress(job: SparkJob | null): { progressPct: number | null; remainingSeconds: number | null } {
  const [now, setNow] = useState(Date.now());
  const displayStatus = useMemo(() => getJobDisplayStatus(job), [job]);
  const isRunning = displayStatus === 'running';

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isRunning]);

  return useMemo(() => {
    if (!job) {
      return { progressPct: null, remainingSeconds: null };
    }

    const totalEstimate = job._expected_total_time_seconds;
    if (!isRunning || !totalEstimate || totalEstimate <= 0) {
      const serverPct = job._progress_pct;
      const remaining =
        totalEstimate != null && serverPct != null && serverPct < 100
          ? totalEstimate * (1 - serverPct / 100)
          : null;
      return { progressPct: serverPct ?? null, remainingSeconds: remaining };
    }

    const startTime = job._progress_started_at
      ? new Date(job._progress_started_at).getTime()
      : new Date(job._created_at).getTime();
    const elapsedSeconds = (now - startTime) / 1000;
    const cappedElapsed = Math.min(elapsedSeconds, totalEstimate);
    const computedPct = Math.min(99, (cappedElapsed / totalEstimate) * 100);
    const remainingSeconds = Math.max(0, totalEstimate - elapsedSeconds);

    return { progressPct: computedPct, remainingSeconds };
  }, [isRunning, job, now]);
}

export function formatEta(seconds?: number | null): string | null {
  if (seconds == null || seconds <= 0) {
    return null;
  }

  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) {
    return `~${minutes}m left`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return `~${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''} left`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `~${days}d${remainingHours > 0 ? ` ${remainingHours}h` : ''} left`;
}

export function formatStageName(name: string): string {
  return name
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatParallelStatus(info: ParallelInfo): string {
  const parts: string[] = [];
  if (info.completedCount > 0) {
    parts.push(`${info.completedCount} completed`);
  }
  if (info.activeCount > 0) {
    parts.push(`${info.activeCount} running`);
  }
  if (info.pendingCount > 0) {
    parts.push(`${info.pendingCount} pending`);
  }
  return parts.join(', ');
}

export function analyzeParallelProgress(updates: SparkJobStatusUpdate[] | undefined): ProgressAnalysis {
  const visibleUpdates = getVisibleUpdates(updates);
  if (visibleUpdates.length === 0) {
    return { latestUpdate: null, parallelInfo: null };
  }

  const latestVisibleScopeUpdates = Array.from(getLatestVisibleScopeUpdates(visibleUpdates).values());
  const activeScopedUpdates = latestVisibleScopeUpdates
    .filter((update) => update.code === 'in_progress')
    .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime());
  const latestActiveScopedUpdate =
    activeScopedUpdates.length > 0 ? activeScopedUpdates[activeScopedUpdates.length - 1] : null;
  const latestUpdate = latestActiveScopedUpdate || visibleUpdates[visibleUpdates.length - 1];
  const stageGroups: Record<string, Record<string, { pending: boolean; started: boolean; completed: boolean }>> = {};

  for (const update of visibleUpdates) {
    if (!update.scope || update.scope === 'overall') {
      continue;
    }

    const branchMetadata = getBranchMetadata(update.scope);
    if (branchMetadata) {
      const { branchScope, stageName } = branchMetadata;

      if (!stageGroups[stageName]) {
        stageGroups[stageName] = {};
      }

      if (!stageGroups[stageName][branchScope]) {
        stageGroups[stageName][branchScope] = { pending: false, started: false, completed: false };
      }

      const group = stageGroups[stageName][branchScope];
      if (update.stage_event === 'stage_pending' || update.code === 'pending') {
        group.pending = true;
        group.started = false;
        group.completed = false;
      }
      if (update.stage_event === 'stage_started' || update.code === 'in_progress') {
        group.started = true;
        group.pending = false;
        group.completed = false;
      }
      if (update.stage_event === 'stage_completed' || update.code === 'completed') {
        group.completed = true;
        group.pending = false;
        group.started = false;
      }
    }
  }

  for (const [stageName, branches] of Object.entries(stageGroups)) {
    const branchKeys = Object.keys(branches);
    if (branchKeys.length <= 1) {
      continue;
    }

    let completedCount = 0;
    let activeCount = 0;
    let pendingCount = 0;

    for (const branchKey of branchKeys) {
      const branch = branches[branchKey];
      if (branch.completed) {
        completedCount += 1;
      } else if (branch.started) {
        activeCount += 1;
      } else if (branch.pending) {
        pendingCount += 1;
      }
    }

    return {
      latestUpdate,
      parallelInfo: {
        stageName: formatStageName(stageName),
        activeCount,
        completedCount,
        pendingCount,
        totalCount: branchKeys.length,
      },
    };
  }

  return { latestUpdate, parallelInfo: null };
}
